import { ResponseCode } from '@/common/request/types';
import { App } from '@/modules/apps/types';
import { applicationService } from '@/services/applications';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAppStore = defineStore(
    'store.apps',
    () => {
        const apps = ref<App[]>([]);
        const appId = ref<number | null>(null);
        const currentApp = computed<App | undefined>(() => apps.value.find(app => app.id === appId.value));

        const appMap = computed(() => new Map(apps.value.map(app => [app.id, app])));
        const appSelectOptions = computed(() => apps.value.map(app => ({ label: app.name, value: app.id })));
        const appSelectOptionsWithAll = computed(() => [{ label: 'All', value: -1 }, ...appSelectOptions.value]);

        function setAppId(newAppId: number): void {
            if (!apps.value.length) {
                return;
            }

            appId.value = apps.value.some(app => app.id === newAppId) ? newAppId : apps.value[0].id;
        }

        function setApps(appList: App[]): void {
            apps.value = appList;

            if (!appId.value && appList.length) {
                appId.value = appList[0].id;
            }
        }

        async function loadApps(): Promise<void> {
            const response = await applicationService.app.list();
            if (response.code === ResponseCode.Ok) {
                setApps(response.data ?? []);
            }
        }

        function getAppById(appId: number): App | undefined {
            return appMap.value.get(appId);
        }

        function getAppNamesByIds(ids: number[]): string {
            return ids
                .map(id => appMap.value.get(id)?.name)
                .filter(Boolean)
                .join(', ');
        }

        return {
            apps,
            appId,
            currentApp,

            appSelectOptions,
            appSelectOptionsWithAll,

            setAppId,
            setApps,
            loadApps,
            getAppById,
            getAppNamesByIds
        };
    },
    {
        persist: true
    }
);
